import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent,
  HttpInterceptor, HttpErrorResponse, HttpContext, HttpContextToken
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService, RefreshResult } from '../services/auth.service';
import { Router } from '@angular/router';
import { NetworkGuardService } from '../services/network-guard.service';
import { PASS_THROUGH_401, NO_NET_TOAST_URL, SAVE_FAILED_TEXT, NGSW_BYPASS } from '../services/session-policy';
import { isApiUrl } from '../utils/sw-cache-guard';

/** Marks a request this interceptor already re-sent once after a 401 (at most ONE auth retry per request). */
export const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * AUTH INTERCEPTOR — attaches the JWT and decides what a 401/403 means (C1, 2026-10-01):
 *  - a request sent under another person or an ended session is dropped, never re-sent with the current token;
 *  - a request that carried an older token is retried once with the current one;
 *  - otherwise it waits for AuthService's ONE shared renewal and retries once;
 *  - SESSION_EXPIRED (SessionTimeoutInterceptor), ACCOUNT_DEACTIVATED and UNLOCK_LOCKED_OUT sign out at once;
 *  - 401s from the public auth endpoints (wrong password / code) go straight back to the caller.
 * Every sign-out goes through AuthService.endSession().
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private networkGuard: NetworkGuardService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // DISC-012 (owner decision 8): the service worker never answers or stores an API call, and an offline failure stays a real
    // network error (status 0) for the offline write queue. First, so the support renewal and every re-send carry it too.
    if (isApiUrl(request.url)) request = request.clone({ setHeaders: { ...NGSW_BYPASS } });

    // Support-session renewal carries the OWNER's own token (set by AuthService) — keep it, drop the marker, and let
    // errors go straight back to the caller (no refresh/logout handling for this one request).
    if (request.headers.has('X-Owner-Auth')) {
      return next.handle(request.clone({ headers: request.headers.delete('X-Owner-Auth'), withCredentials: true }));
    }

    // Skip auth header for refresh and login endpoints
    const isAuthRequest = request.url.includes('/auth/login') || request.url.includes('/auth/refresh');

    const token = this.authService.token;

    // Attach JWT token to every request automatically.
    // ALSO enable withCredentials so the browser includes the HttpOnly
    // refresh-token cookie on /auth/* calls. Without this, the cookie set
    // by the API would be ignored cross-origin (e.g., 4200 → 5253 in dev).
    const cloneOpts: any = { withCredentials: true };
    if (token && !isAuthRequest) {
      cloneOpts.setHeaders = { Authorization: `Bearer ${token}` };
    }
    request = request.clone(cloneOpts);

    return this.send(request, next);
  }

  private send(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const code = error.error?.code;

        // (0) L1: 5 wrong passwords (lock screen / change password) — the server already ended this device's
        // session, so no renewal. In a support tab this signs out fully (the owner's own session on this device).
        if (error.status === 401 && code === 'UNLOCK_LOCKED_OUT') {
          if (this.authService.isLoggedIn) {
            this.authService.endSession({ kind: 'forced', code: 'UNLOCK_LOCKED_OUT', message: error.error?.message, fromSupportExit: true });
          }
          return EMPTY;
        }

        // (1) The account was switched off — sign out with that reason, no renewal.
        if (error.status === 401 && code === 'ACCOUNT_DEACTIVATED' && !PASS_THROUGH_401.test(request.url)) {
          if (this.authService.isLoggedIn) {
            this.authService.endSession({ kind: 'forced', code: 'ACCOUNT_DEACTIVATED', message: error.error?.message });
          }
          return EMPTY;
        }

        // (2) Any other 401 outside the public auth endpoints. Those (login, verify-mfa, resend-mfa, refresh,
        // forgot-password*, set-password, verify-password) fall through to the caller untouched (TC-0053).
        if (error.status === 401 && !PASS_THROUGH_401.test(request.url)) {
          return this.on401(request, next, error);
        }

        // (3) ── Must change password — re-check with the server first (a 403 from a request sent just before a
        // successful change must not force a second change), then patch the flag so lockedRouteGuard allows the
        // forced page and it shows the forced variant.
        if (error.status === 403 && error.error?.mustChangePassword) {
          if (this.authService.isLoggedIn) {
            this.authService.recheckMustChangePassword().subscribe(still => {
              if (!still) return;
              if (!this.authService.currentUser?.mustChangePassword) this.authService.patchCurrentUser({ mustChangePassword: true });
              this.router.navigate(['/change-password']);
            });
          }
          return EMPTY;
        }

        // (4) Platform owner suspended this hospital → end the session and explain why on the login page.
        if (error.status === 403 && code === 'HOSPITAL_SUSPENDED') {
          if (this.authService.isLoggedIn) {
            this.authService.endSession({ kind: 'forced', code: 'HOSPITAL_SUSPENDED', message: error.error?.message });
          }
          return EMPTY;
        }

        // (5) Read-only hospital: the write was refused — remember it so the app-wide banner shows at once.
        if (error.status === 403 && code === 'HOSPITAL_READ_ONLY') {
          const cur = this.authService.currentUser;
          if (cur && cur.hospitalAccess !== 'ReadOnly') {
            this.authService.patchCurrentUser({ hospitalAccess: 'ReadOnly', hospitalAccessReason: error.error?.reason ?? null });
          }
        }

        // Feature gate
        if (error.status === 403 && error.error?.requiredFeature) {
          console.warn('Feature gated:', error.error.requiredFeature, error.error.message);
        }

        // (6) ── Network failure detection ── Offline change-password still toasts (TC-0082); logout, logout-all
        // and refresh never do (DISC-011).
        if (error.status === 0 && this.isWriteRequest(request) && !NO_NET_TOAST_URL.test(request.url)) {
          this.networkGuard.showToast(SAVE_FAILED_TEXT, 'error');
        }

        return throwError(() => error);
      })
    );
  }

  /** A 401 on a data call: drop, retry with the current token, or wait for the shared renewal and retry once. */
  private on401(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    const auth = this.authService;
    const user = auth.currentUser;
    // The session already ended: swallow, with no second sign-out and no broadcast.
    if (!user) return EMPTY;

    // Support session: the hospital ended its approval (or it ran out) → back to the owner console. During an exit
    // this is a no-op (endSession's exitingSupport guard) and finish() returns the owner to the console.
    if (user.support && (error.error?.code === 'SUPPORT_SESSION_ENDED' || request.url.includes('/support-session/'))) {
      auth.handleSessionLost(error.error?.message);
      return EMPTY;
    }

    // At most ONE auth retry per request; a second 401 goes back to the caller.
    if (request.context.get(AUTH_RETRIED)) return throwError(() => error);

    const sent = (request.headers.get('Authorization') || '').replace(/^Bearer\s+/i, '');
    // The request carried no sign-in; nothing to renew.
    if (!sent) return throwError(() => error);

    // Sent under a session that has ended or under another person (shared PC: 'Switch user' and the next nurse
    // signed in). It is NEVER re-sent with the current token.
    if (!auth.isCurrentIdentity(sent)) return EMPTY;

    // A renewal finished while this request was in flight (TC-0058 'arrives after'): no second refresh.
    if (sent !== auth.token) return this.retry(request, next, auth.token);

    return auth.refreshSession('401').pipe(
      switchMap((r: RefreshResult) => {
        if (r.ok) return this.retry(request, next, r.user.token);
        // A terminal result already ended the session once (session-scope broadcast only): waiters complete silently.
        if (r.terminal || !auth.isLoggedIn) return EMPTY;
        return this.networkFailure(request, r);
      })
    );
  }

  /** A transient renewal failure reaches the caller as status 0 (or the 5xx) for ITS OWN URL — never a raw
   *  TimeoutError or the /auth/refresh URL — so offline-queue code that checks err.status === 0 keeps working.
   *  Its `error` is only the refresh reply's BODY (a JSON object, or the network ProgressEvent), never the refresh
   *  HttpErrorResponse itself: screens show `err.error?.message`, and that object's message is Angular's
   *  'Http failure response for …/api/auth/refresh: 503 …'. A text body (an HTML error page) or a timeout gives null. */
  private networkFailure(request: HttpRequest<any>, r: Extract<RefreshResult, { ok: false }>): Observable<never> {
    const status = r.status && r.status >= 500 ? r.status : 0;
    const inner = r.error instanceof HttpErrorResponse ? r.error.error : null;
    const body = inner != null && typeof inner === 'object' ? inner : null;
    const err = new HttpErrorResponse({ status, statusText: 'Unknown Error', url: request.url, error: body });
    if (status === 0 && this.isWriteRequest(request) && !NO_NET_TOAST_URL.test(request.url)) {
      this.networkGuard.showToast(SAVE_FAILED_TEXT, 'error');
    }
    return throwError(() => err);
  }

  /** Re-send once with `token`, through send() so a retried write that fails offline still toasts and a retried 403
   *  must-change still redirects. A NEW HttpContext: HttpContext.set mutates and clone() shares it, so mutating it
   *  would mark the caller's original request as already retried. */
  private retry(req: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    const ctx = new HttpContext();
    for (const k of req.context.keys()) ctx.set(k, req.context.get(k));
    ctx.set(AUTH_RETRIED, true);
    return this.send(req.clone({ setHeaders: { Authorization: `Bearer ${token}` }, context: ctx }), next);
  }

  /** Check if this is a data-modifying request (POST/PUT/PATCH/DELETE).
   *  AUDIT FIX (2026-06-16): DELETE was missing here while ErrorInterceptor's isWrite DID include
   *  it — so ErrorInterceptor suppressed its status-0 "network error" toast for a DELETE (treating
   *  it as a write it delegates to us), but this interceptor then never fired its "Save failed — no
   *  network" toast for DELETE → a connection-dropped DELETE got ZERO feedback. Aligning the sets. */
  private isWriteRequest(req: HttpRequest<any>): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
  }
}
